import { useCallback, useEffect, useState } from 'react';

import {
  BlogPublicPost,
  blogImageUrl,
  blogPostHref,
  blogReadingMinutes,
  formatBlogRelativeDate,
} from '@/lib/entities/blog';

import { ArrowIcon } from '@/lib/shared/icons';

import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { BlogCover } from './BlogCover';
import styles from './FeaturedPost.module.scss';

type FeaturedPostProps = {
  posts: BlogPublicPost[];
  locale: string;
};

type FeaturedSlideProps = {
  post: BlogPublicPost;
  locale: string;
  isPriority: boolean;
};

const FeaturedSlide = ({ post, locale, isPriority }: FeaturedSlideProps) => {
  const { t } = useTranslation('index', { keyPrefix: 'blog' });
  const imageUrl = blogImageUrl(post.imageKey);
  const minutes = blogReadingMinutes(post);
  const relative = formatBlogRelativeDate(post.publishedOn, locale);
  const reading = t('readingTimeFull', { count: minutes });
  const meta = relative
    ? `${reading} • ${t('updatedAgo', { time: relative })}`
    : reading;

  return (
    <article className={styles.featured__inner}>
      <BlogCover
        imageUrl={imageUrl}
        className={styles.featured__media}
        sizes="(max-width: 995px) 100vw, 664px"
        priority={isPriority}
      />
      <div className={styles.featured__content}>
        <div className={styles.featured__copy}>
          <p className={styles.featured__meta}>{meta}</p>
          <h2 className={styles.featured__title}>{post.pageTitle}</h2>
          {post.introText ? (
            <p className={styles.featured__excerpt}>{post.introText}</p>
          ) : null}
        </div>
        <Link href={blogPostHref(post)} className={styles.featured__cta}>
          {t('readArticle')}
        </Link>
      </div>
    </article>
  );
};

export const FeaturedPost = ({ posts, locale }: FeaturedPostProps) => {
  const { t } = useTranslation('index', { keyPrefix: 'blog' });
  const canScroll = posts.length > 1;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: canScroll,
    duration: 20,
    watchDrag: canScroll,
  });

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', handleSelect);
    handleSelect();

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi]);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  if (posts.length === 0) {
    return null;
  }

  const currentTitle = posts[selectedIndex]?.pageTitle ?? posts[0].pageTitle;

  return (
    <section
      className={styles.featured}
      aria-roledescription="carousel"
      aria-label={t('featuredLabel')}
    >
      <div className={styles.featured__viewport} ref={emblaRef}>
        <div className={styles.featured__container}>
          {posts.map((post, index) => (
            <div
              key={post.id}
              className={styles.featured__slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${posts.length}`}
              aria-hidden={index !== selectedIndex}
              {...(index !== selectedIndex ? { inert: '' } : {})}
            >
              <FeaturedSlide
                post={post}
                locale={locale}
                isPriority={index === 0}
              />
            </div>
          ))}
        </div>
      </div>

      {canScroll ? (
        <>
          <button
            type="button"
            className={styles.featured__arrowPrev}
            aria-label={t('featuredPrev')}
            onClick={handlePrev}
          >
            <ArrowIcon className={styles.featured__arrowIcon} />
          </button>
          <button
            type="button"
            className={styles.featured__arrowNext}
            aria-label={t('featuredNext')}
            onClick={handleNext}
          >
            <ArrowIcon isRight className={styles.featured__arrowIcon} />
          </button>
        </>
      ) : null}

      <p className={styles.featured__live} aria-live="polite">
        {currentTitle}
      </p>
    </section>
  );
};

export default FeaturedPost;
